// StadiumMap.tsx - Mercedes-Benz Stadium Layout (Theme-Aware)
import React, { useState, useMemo } from 'react';
import styles from './StadiumMap.module.css';

const DEFAULT_SECTION_GROUPS = {
  lower: {
    north: ['101', '102', '103', '104', '105', '106', '107', '108', '109', '110', '111'],
    east: ['112', '113', '114', '115', '116', '117', '118', '119', '120', '121', '122', '123', '124'],
    south: ['125', '126', '127', '128', '129', '130', '131', '132', '133', '134', '135'],
    west: ['136', '137', '138', '139', '140', '141', '142', '143', '144', '145', '146', '147', '148']
  },
  club: {
    north: ['201', '202', '203', '204', '205', '206', '207', '208', '209', '210', '211'],
    east: ['212', '213', '214', '215', '216', '217', '218', '219', '220', '221', '222'],
    south: ['223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233'],
    west: ['234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244']
  },
  upper: {
    north: ['301', '302', '303', '304', '305', '306', '307', '308', '309', '310', '311'],
    east: ['312', '313', '314', '315', '316', '317', '318', '319', '320', '321', '322', '323', '324'],
    south: ['325', '326', '327', '328', '329', '330', '331', '332', '333', '334', '335'],
    west: ['336', '337', '338', '339', '340', '341', '342', '343', '344', '345', '346', '347', '348']
  }
};

function buildGroupsFromList(sectionList: string[]) {
  const lower = sectionList.filter((s) => /^1\d{2}$/.test(s));
  const club = sectionList.filter((s) => /^2\d{2}$/.test(s));
  const upper = sectionList.filter((s) => /^3\d{2}$/.test(s));
  const splitQuadrants = (sections: string[]) => {
    const chunk = Math.ceil(sections.length / 4) || 1;
    return {
      north: sections.slice(0, chunk),
      east: sections.slice(chunk, chunk * 2),
      south: sections.slice(chunk * 2, chunk * 3),
      west: sections.slice(chunk * 3),
    };
  };
  return {
    lower: lower.length ? splitQuadrants(lower) : DEFAULT_SECTION_GROUPS.lower,
    club: club.length ? splitQuadrants(club) : DEFAULT_SECTION_GROUPS.club,
    upper: upper.length ? splitQuadrants(upper) : DEFAULT_SECTION_GROUPS.upper,
  };
}

function countSelectionsForLevel(level: 'lower' | 'club' | 'upper', sections: Set<string>) {
  const pattern = level === 'lower' ? /^1\d{2}$/ : level === 'club' ? /^2\d{2}$/ : /^3\d{2}$/;
  return Array.from(sections).filter((s) => pattern.test(s)).length;
}

function getInitialLevel(selectedSections: Set<string>): 'lower' | 'club' | 'upper' {
  const counts = {
    lower: countSelectionsForLevel('lower', selectedSections),
    club: countSelectionsForLevel('club', selectedSections),
    upper: countSelectionsForLevel('upper', selectedSections),
  };
  const max = Math.max(counts.lower, counts.club, counts.upper);
  if (max === 0) return 'lower';
  if (counts.lower === max) return 'lower';
  if (counts.club === max) return 'club';
  return 'upper';
}

interface StadiumMapProps {
  selectedSections: Set<string>;
  onSectionToggle: (sections: Set<string>) => void;
  sectionList?: string[];
}

const StadiumMap: React.FC<StadiumMapProps> = ({ selectedSections, onSectionToggle, sectionList }) => {
  const [selectedLevel, setSelectedLevel] = useState<'lower' | 'club' | 'upper'>(() =>
    getInitialLevel(selectedSections)
  );

  const allSectionGroups = useMemo(
    () => (sectionList?.length ? buildGroupsFromList(sectionList) : DEFAULT_SECTION_GROUPS),
    [sectionList]
  );
  
  const toggleSection = (section: string) => {
    const newSections = new Set(selectedSections);
    if (newSections.has(section)) {
      newSections.delete(section);
    } else {
      newSections.add(section);
    }
    onSectionToggle(newSections);
  };

  // Level options for dropdown
  const levelOptions = [
    { value: 'lower', label: 'Lower Level - 100s', description: 'Field level seating', color: '#3b82f6' },
    { value: 'club', label: 'Club Level - 200s', description: 'Premium club seating', color: '#f44336' },
    { value: 'upper', label: 'Upper Level - 300s', description: 'Upper deck seating', color: '#64b5f6' }
  ];

  // All stadium sections organized by level
  const currentSections = allSectionGroups[selectedLevel];

  const handleLevelChange = (newLevel: 'lower' | 'club' | 'upper') => {
    setSelectedLevel(newLevel);
  };

  const getTotalSectionsForLevel = (level: 'lower' | 'club' | 'upper') => {
    const sections = allSectionGroups[level];
    return Object.values(sections).flat().length;
  };

  // Section component with improved spacing
  const Section = ({ section, x, y, isSelected }) => {
    const levelOption = levelOptions.find(opt => opt.value === selectedLevel);
    const levelColor = levelOption.color;

    return (
      <g key={section} style={{ cursor: 'pointer' }} onClick={() => toggleSection(section)}>
        <rect
          x={x - 16}
          y={y - 10}
          width={32}
          height={20}
          rx="4"
          style={{
            fill: isSelected ? levelColor : 'var(--color-surface-secondary)',
            stroke: levelColor,
            strokeWidth: isSelected ? 2 : 1,
            filter: isSelected ? `drop-shadow(0 0 6px ${levelColor}66)` : 'none',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            if (!isSelected) {
              (e.target as SVGElement).style.fill = 'var(--color-border-hover)';
            }
          }}
          onMouseOut={(e) => {
            if (!isSelected) {
              (e.target as SVGElement).style.fill = 'var(--color-surface-secondary)';
            }
          }}
        />
        <text
          x={x}
          y={y + 2}
          textAnchor="middle"
          style={{
            fill: isSelected ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
            fontSize: '9px',
            fontWeight: isSelected ? 'bold' : '600',
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        >
          {section}
        </text>
      </g>
    );
  };

  // Get level display info
  const getLevelInfo = () => {
    return levelOptions.find(opt => opt.value === selectedLevel);
  };

  const levelInfo = getLevelInfo();

  return (
    <div className={styles.stadiumMap}>
      
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <h3 style={{
          margin: '0 0 8px 0',
          fontSize: '24px',
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Mercedes-Benz Stadium - Atlanta
        </h3>
        <p style={{
          margin: 0,
          color: 'var(--color-text-tertiary)',
          fontSize: '14px'
        }}>
          Select sections for vendor service coverage
        </p>
      </div>

      {/* Level Switcher - Moved to top */}
      <div style={{
        backgroundColor: 'var(--color-surface-secondary)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid var(--color-border-primary)'
      }}>
        <p style={{
          margin: '0 0 12px 0',
          color: 'var(--color-text-primary)',
          fontSize: '14px',
          fontWeight: '600',
          textAlign: 'center'
        }}>Service Level Selection</p>
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          {levelOptions.map((option) => {
            const levelCount = countSelectionsForLevel(
              option.value as 'lower' | 'club' | 'upper',
              selectedSections
            );
            const label =
              levelCount > 0 ? `${option.label} (${levelCount})` : option.label;

            return (
            <button
              key={option.value}
              onClick={() => handleLevelChange(option.value as 'lower' | 'club' | 'upper')}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: `2px solid ${option.color}`,
                borderRadius: '8px',
                backgroundColor: selectedLevel === option.value ? option.color : 'var(--color-surface-primary)',
                color: selectedLevel === option.value ? '#ffffff' : 'var(--color-text-secondary)',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                if (selectedLevel !== option.value) {
                  (e.target as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
                  (e.target as HTMLElement).style.color = 'var(--color-text-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedLevel !== option.value) {
                  (e.target as HTMLElement).style.backgroundColor = 'var(--color-surface-primary)';
                  (e.target as HTMLElement).style.color = 'var(--color-text-secondary)';
                }
              }}
            >
              {label}
            </button>
            );
          })}
        </div>
        <p style={{
          margin: '12px 0 0 0',
          color: 'var(--color-text-tertiary)',
          fontSize: '12px',
          fontStyle: 'italic',
          textAlign: 'center'
        }}>
          {levelInfo.description}
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
        <svg
          width="850"
          height="470"
          viewBox="0 0 850 470"
          style={{
            background: 'radial-gradient(ellipse at center, var(--color-surface-secondary) 0%, var(--color-background-primary) 100%)',
            borderRadius: '12px',
            border: '2px solid var(--color-border-primary)',
            maxWidth: '100%',
            height: 'auto',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          {/* Outer stadium bowl */}
          <ellipse cx="425" cy="235" rx="390" ry="205" style={{
            fill: 'none',
            stroke: 'var(--color-border-hover)',
            strokeWidth: 2,
            opacity: 0.6
          }} />

          {/* Current level highlight */}
          <ellipse
            cx="425"
            cy="235"
            rx="330"
            ry="175"
            style={{
              fill: 'none',
              stroke: levelInfo.color,
              strokeWidth: 3,
              opacity: 0.8
            }}
          />

          {/* Lower bowl reference */}
          <ellipse cx="425" cy="235" rx="235" ry="130" style={{
            fill: 'none',
            stroke: 'var(--color-border-primary)',
            strokeWidth: 1,
            opacity: 0.3
          }} />

          {/* Playing field — horizontal */}
          <rect x="248" y="140" width="354" height="190" rx="15" style={{
            fill: 'rgba(76, 175, 80, 0.15)',
            stroke: '#4caf50',
            strokeWidth: 2
          }} />

          {/* Field markings — vertical yard lines */}
          {Array.from({ length: 10 }, (_, i) => {
            const xPos = 280 + i * 32;
            return (
              <line
                key={i}
                x1={xPos}
                y1="140"
                x2={xPos}
                y2="330"
                style={{
                  stroke: '#4caf50',
                  strokeWidth: 1,
                  opacity: 0.8
                }}
              />
            );
          })}

          {/* Atlanta Falcons Logo — left end zone */}
          <image x="100" y="190" width="120" height="80"
            href="https://iconlogovector.com/uploads/images/2025/01/lg-679c36c109d93-Atlanta-Falcons.webp" />

          {/* Atlanta Falcons Logo — right end zone */}
          <image x="630" y="190" width="120" height="80"
            href="https://iconlogovector.com/uploads/images/2025/01/lg-679c36c109d93-Atlanta-Falcons.webp" />

          {/* North End Zone sections — left arc */}
          {currentSections.north.map((section, index) => {
            const isSelected = selectedSections.has(section);
            const totalSections = currentSections.north.length;
            const angleSpan = 74;
            const startAngle = 143;
            const angle = startAngle + (index / (totalSections - 1)) * angleSpan;
            const x = 425 + Math.cos(angle * Math.PI / 180) * 330;
            const y = 235 + Math.sin(angle * Math.PI / 180) * 175;
            return <Section key={section} section={section} x={x} y={y} isSelected={isSelected} />;
          })}

          {/* East Sideline sections — top arc */}
          {currentSections.east.map((section, index) => {
            const isSelected = selectedSections.has(section);
            const totalSections = currentSections.east.length;
            const angleSpan = 106;
            const startAngle = 217;
            const angle = startAngle + (index / (totalSections - 1)) * angleSpan;
            const x = 425 + Math.cos(angle * Math.PI / 180) * 330;
            const y = 235 + Math.sin(angle * Math.PI / 180) * 175;
            return <Section key={section} section={section} x={x} y={y} isSelected={isSelected} />;
          })}

          {/* South End Zone sections — right arc */}
          {currentSections.south.map((section, index) => {
            const isSelected = selectedSections.has(section);
            const totalSections = currentSections.south.length;
            const angleSpan = 74;
            const startAngle = -37;
            const angle = startAngle + (index / (totalSections - 1)) * angleSpan;
            const x = 425 + Math.cos(angle * Math.PI / 180) * 330;
            const y = 235 + Math.sin(angle * Math.PI / 180) * 175;
            return <Section key={section} section={section} x={x} y={y} isSelected={isSelected} />;
          })}

          {/* West Sideline sections — bottom arc */}
          {currentSections.west.map((section, index) => {
            const isSelected = selectedSections.has(section);
            const totalSections = currentSections.west.length;
            const angleSpan = 106;
            const startAngle = 37;
            const angle = startAngle + (index / (totalSections - 1)) * angleSpan;
            const x = 425 + Math.cos(angle * Math.PI / 180) * 330;
            const y = 235 + Math.sin(angle * Math.PI / 180) * 175;
            return <Section key={section} section={section} x={x} y={y} isSelected={isSelected} />;
          })}

          {/* Stadium Labels */}
          <text x="30" y="235" textAnchor="middle" transform="rotate(-90 30 235)" style={{
            fill: 'var(--color-text-tertiary)',
            fontSize: '11px',
            fontWeight: '600',
            opacity: 0.8
          }}>NORTH END ZONE</text>
          <text x="425" y="22" textAnchor="middle" style={{
            fill: 'var(--color-text-tertiary)',
            fontSize: '11px',
            fontWeight: '600',
            opacity: 0.8
          }}>EAST SIDELINE</text>
          <text x="820" y="235" textAnchor="middle" transform="rotate(90 820 235)" style={{
            fill: 'var(--color-text-tertiary)',
            fontSize: '11px',
            fontWeight: '600',
            opacity: 0.8
          }}>SOUTH END ZONE</text>
          <text x="425" y="460" textAnchor="middle" style={{
            fill: 'var(--color-text-tertiary)',
            fontSize: '11px',
            fontWeight: '600',
            opacity: 0.8
          }}>WEST SIDELINE</text>

          {/* Level indicator */}
          <text x="425" y="240" textAnchor="middle" style={{
            fill: levelInfo.color,
            fontSize: '13px',
            fontWeight: 'bold'
          }}>
            {levelInfo.label}
          </text>
        </svg>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px',
        background: 'var(--gradient-card)',
        borderRadius: '12px',
        border: '1px solid var(--color-border-primary)',
        marginBottom: '20px',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{ flex: 1 }}>
          <p style={{
            margin: '0 0 4px 0',
            color: 'var(--color-text-tertiary)',
            fontSize: '14px',
            fontWeight: '500'
          }}>Selected Sections</p>
          <p style={{
            margin: '0 0 4px 0',
            color: 'var(--color-text-primary)',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            {selectedSections.size} of {getTotalSectionsForLevel(selectedLevel)} sections
          </p>
          <p style={{
            margin: 0,
            color: 'var(--color-text-tertiary)',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {levelInfo.label}
          </p>
        </div>
        
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          maxWidth: '400px',
          maxHeight: '100px',
          overflowY: 'auto'
        }}>
          {Array.from(selectedSections).length > 0 ? (
            Array.from(selectedSections).sort((a, b) => {
              const aNum = parseInt(String(a).replace(/\D/g, '')) || 0;
              const bNum = parseInt(String(b).replace(/\D/g, '')) || 0;
              return aNum - bNum;
            }).map(section => (
              <span key={String(section)} style={{
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                backgroundColor: levelInfo.color + '33',
                color: levelInfo.color,
                border: `1px solid ${levelInfo.color}`
              }}>
                {String(section)}
              </span>
            ))
          ) : (
            <span style={{
              color: 'var(--color-text-tertiary)',
              fontSize: '14px',
              fontStyle: 'italic'
            }}>No sections selected</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default StadiumMap;